<template>
  <div class="report-list">
    <table class="styled-table">
      <thead>
        <tr>
          <th class="section-header"
              @click="isVisible = !isVisible"
          >
            {{ isVisible ? '▼' : '▲' }} {{ $t('dashboardPage.reports') }}
          </th>
        </tr>
      </thead>
    </table>

    <div v-show="isVisible">
      <div class="toolbar">
        <div class="filters">
          <select v-if="customers.length > 1"
                  v-model="customerId"
                  :aria-label="$t('dashboardPage.allCustomers')"
                  @change="onCustomerChange"
          >
            <option :value="null">
              {{ $t('dashboardPage.allCustomers') }}
            </option>
            <option v-for="customer in customers"
                    :key="customer.Id"
                    :value="customer.Id"
            >
              {{ customer.Name }}
            </option>
          </select>
          <select v-model="siteId"
                  :aria-label="$t('dashboardPage.allSites')"
                  @change="fetchDocuments(1)"
          >
            <option :value="null">
              {{ $t('dashboardPage.allSites') }}
            </option>
            <option v-for="site in sites"
                    :key="site.Id"
                    :value="site.Id"
            >
              {{ site.Name }} ({{ site.Count }})
            </option>
          </select>
          <input v-model="search"
                 type="search"
                 :placeholder="$t('dashboardPage.searchReports')"
                 :aria-label="$t('dashboardPage.searchReports')"
                 @input="onSearchInput"
          >
        </div>
      </div>

      <table class="styled-table">
        <colgroup>
          <col>
          <col>
          <col class="task-column">
          <col class="icon-column">
        </colgroup>
        <thead>
          <tr>
            <th v-for="column in columns"
                :key="column.key"
                class="sortable"
                :aria-sort="sortColumn === column.key ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'"
                @click="sortTable(column.key)"
            >
              {{ column.label }}
              <span v-if="sortColumn === column.key">
                {{ sortOrder === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="4"
                class="loading-cell"
            >
              <div class="spinner" />
            </td>
          </tr>
          <tr v-else-if="documents.length === 0">
            <td colspan="4"
                class="empty-cell"
            >
              {{ $t('dashboardPage.noReports') }}
            </td>
          </tr>
          <template v-else>
            <tr v-for="document in documents"
                :key="`${document.TaskId ?? 'site'}-${document.SiteId}-${document.Id}`"
                class="documentRow"
            >
              <td class="tdDocument"
                  :title="document.CustomerName"
              >
                {{ document.CustomerName }}
              </td>
              <td class="tdDocument"
                  :title="document.SiteName"
              >
                <router-link v-if="document.SiteId"
                             :to="`/objects/${document.SiteId}`"
                >
                  {{ document.SiteName }}
                </router-link>
              </td>
              <td class="tdDocument">
                <router-link v-if="document.TaskId"
                             :to="`/tasks/${document.TaskId}`"
                >
                  {{ document.TaskNr }}
                </router-link>
              </td>
              <td class="tdDocument icon-cell">
                <button class="icon-button"
                        :disabled="downloadingId === document.Id"
                        :title="documentTitle(document)"
                        :aria-label="`${$t('dashboardPage.download')} ${documentTitle(document)}`"
                        @click="download(document)"
                >
                  <span class="material-icons">{{ documentIcon(document) }}</span>
                </button>
                <button v-if="canRemoveDocument(store, document)"
                        class="icon-button remove-button"
                        :title="$t('documentPage.remove')"
                        :aria-label="`${$t('documentPage.remove')} ${documentTitle(document)}`"
                        @click="removeDocument(document)"
                >
                  <span class="material-icons">delete</span>
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-if="totalCount > 0"
           class="pager"
      >
        <button :disabled="isLoading || currentPage <= 1"
                @click="fetchDocuments(currentPage - 1)"
        >
          {{ $t('dashboardPage.previous') }}
        </button>
        <span>
          {{ $t('dashboardPage.pageInfo', { from: pageFrom, to: pageTo, total: totalCount }) }}
        </span>
        <button :disabled="isLoading || currentPage >= totalPages"
                @click="fetchDocuments(currentPage + 1)"
        >
          {{ $t('dashboardPage.next') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import i18n from '@/locales/i18n'
import { GetDocumentListing, GetDocumentListingSites, GetDocument } from '@/api/document.js'
import { downloadBase64Document } from '@/utils/download.js'
import { canRemoveDocument, confirmAndRemoveDocument } from '@/utils/removeDocument.js'
import { loadJsonPreference, saveJsonPreference } from '@/utils/preferences.js'

const reportCategory = 'REPORT'
const pageSize = 10

const store = useStore()
const toast = useToast()

// The report column orders by date, which is shown in its tooltip
const columns = computed(() => [
  { key: 'Customer', label: i18n.global.t('taskPage.customer') },
  { key: 'Site', label: i18n.global.t('taskPage.object1') },
  { key: 'TaskNr', label: i18n.global.t('taskPage.taskNumber') },
  { key: 'Modified', label: i18n.global.t('dashboardPage.report') }
])

// Filters and sorting saved from an earlier session (UK/GB02), checked before use
const filtersPreference = 'dashboard.reports'
const saved = loadJsonPreference(filtersPreference) ?? {}
const savedSortValid = ['Customer', 'Site', 'TaskNr', 'Modified'].includes(saved.sortColumn) && ['asc', 'desc'].includes(saved.sortOrder)

const isVisible = ref(true)
const sortColumn = ref(savedSortValid ? saved.sortColumn : 'Modified')
const sortOrder = ref(savedSortValid ? saved.sortOrder : 'desc')
const customerId = ref(Number.isInteger(saved.customerId) ? saved.customerId : null)
const siteId = ref(Number.isInteger(saved.siteId) ? saved.siteId : null)
const search = ref(typeof saved.search === 'string' ? saved.search.slice(0, 100) : '')
const sites = ref([])
const documents = ref([])
const totalCount = ref(0)
const currentPage = ref(1)
const isLoading = ref(false)
const downloadingId = ref(null)

const customers = computed(() => store.state.account.me?.Customers ?? [])
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize)))
const pageFrom = computed(() => (currentPage.value - 1) * pageSize + 1)
const pageTo = computed(() => Math.min(currentPage.value * pageSize, totalCount.value))

// Responses can arrive out of order when filters change quickly, only the latest request is applied
let documentsRequest = 0
let sitesRequest = 0
let searchTimer = null

function saveFilters () {
  saveJsonPreference(filtersPreference, {
    customerId: customerId.value,
    siteId: siteId.value,
    search: search.value.trim(),
    sortColumn: sortColumn.value,
    sortOrder: sortOrder.value
  })
}

async function fetchDocuments (page) {
  // Every filter or sorting change ends up here
  saveFilters()
  const request = ++documentsRequest
  isLoading.value = true
  try {
    const response = await GetDocumentListing({
      category: reportCategory,
      customerId: customerId.value,
      objectId: siteId.value,
      search: search.value.trim(),
      orderBy: sortColumn.value,
      descending: sortOrder.value === 'desc',
      page: page - 1,
      pageSize
    })
    if (request !== documentsRequest) {
      return
    }
    documents.value = response.data.Documents ?? []
    totalCount.value = response.data.Count ?? 0
    currentPage.value = page
  } catch (e) {
    if (request === documentsRequest) {
      documents.value = []
      totalCount.value = 0
      toast.warning(i18n.global.t('taskPage.couldNotGetData'))
    }
  } finally {
    if (request === documentsRequest) {
      isLoading.value = false
    }
  }
}

async function fetchSites () {
  const request = ++sitesRequest
  try {
    const response = await GetDocumentListingSites({
      category: reportCategory,
      customerId: customerId.value
    })
    if (request === sitesRequest) {
      sites.value = response.data ?? []
      // A saved site that no longer has reports (or isn't the user's any more) is dropped
      if (siteId.value && !sites.value.some(site => site.Id === siteId.value)) {
        siteId.value = null
        fetchDocuments(1)
      }
    }
  } catch (e) {
    if (request === sitesRequest) {
      sites.value = []
    }
  }
}

function sortTable (column) {
  if (sortColumn.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortOrder.value = 'asc'
  }
  fetchDocuments(1)
}

function onCustomerChange () {
  siteId.value = null
  fetchSites()
  fetchDocuments(1)
}

function onSearchInput () {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => fetchDocuments(1), 300)
}

function documentTitle (documentInfo) {
  return `${documentInfo.Name}.${documentInfo.Extension} (${documentInfo.Modified?.substring(0, 10) ?? ''})`
}

function documentIcon (documentInfo) {
  if (downloadingId.value === documentInfo.Id) {
    return 'hourglass_empty'
  }
  return 'description'
}

async function removeDocument (documentInfo) {
  if (await confirmAndRemoveDocument(documentInfo, toast)) {
    // Stay on the page unless it was its last row
    const page = documents.value.length === 1 && currentPage.value > 1 ? currentPage.value - 1 : currentPage.value
    fetchSites()
    fetchDocuments(page)
  }
}

async function download (documentInfo) {
  downloadingId.value = documentInfo.Id
  try {
    const response = await GetDocument(documentInfo.Id)
    downloadBase64Document(response.data)
  } catch (e) {
    toast.error(i18n.global.t('dashboardPage.downloadFailed'))
  } finally {
    downloadingId.value = null
  }
}

onMounted(async () => {
  if (!store.state.account.me?.Customers) {
    await store.dispatch('account/getMe').catch(() => {})
  }
  // A saved customer the user no longer has, or one that can't be seen because the dropdown is hidden, is dropped
  if (customerId.value && (customers.value.length <= 1 || !customers.value.some(customer => customer.Id === customerId.value))) {
    customerId.value = null
    siteId.value = null
  }
  fetchSites()
  fetchDocuments(1)
})

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
})
</script>

<style lang="scss" scoped>
.report-list {
  text-align: left;
}

.styled-table {
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0;
  font-size: 0.9em;
  font-family: 'Lexend', sans-serif;
  width: 100%;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.styled-table thead tr {
  background-color: var(--mainBlue);
  color: white;
  text-align: left;
}

.styled-table th {
  padding: 8px 15px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-header,
.sortable {
  cursor: pointer;
}

.task-column {
  width: 22%;
}

// Fits the "Report" header and both the report and remove icons
.icon-column {
  width: 120px;
}

.toolbar {
  padding: 0.5rem 0;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  select,
  input {
    padding: 0.4rem 0.6rem;
    border-radius: 4px;
    border: 1px solid #ccc;
    font-family: inherit;
    font-size: 0.9rem;
    flex: 1 1 160px;
    max-width: 260px;
    min-width: 0;
  }

  // Room for the placeholder, which names everything searched
  input {
    max-width: 380px;
  }
}

.documentRow {
  border-bottom: 1px solid lightgrey;
  background-color: white;
  color: black;
}

.documentRow:nth-of-type(even) {
  background-color: #c6e3f2;
}

.documentRow:last-of-type {
  border-bottom: 3px solid var(--mainBlue);
}

.tdDocument {
  padding: 8px 15px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  a {
    color: inherit;
  }
}

.icon-cell {
  text-align: center;
  padding: 4px 10px;
}

.icon-button.remove-button {
  margin-left: 8px;
  color: #b3261e;
}

.icon-button {
  border: none;
  background: none;
  cursor: pointer;
  color: var(--mainBlue);
  padding: 0;
  line-height: 0;

  &:disabled {
    cursor: wait;
  }
}

.loading-cell,
.empty-cell {
  padding: 24px 0;
  background-color: #f0f0f0;
  text-align: center;
}

.spinner {
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--mainBlue);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
  margin: auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 0.75rem 0 0.25rem;

  button {
    padding: 0.4rem 0.9rem;
    background-color: var(--mainBlue);
    color: white;
    border: none;
    cursor: pointer;
    border-radius: 4px;
    font-family: inherit;
  }

  button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
}
</style>
